import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '../../components/ui/Badge/Badge';
import { Button } from '../../components/ui/Button/Button';
import { Table } from '../../components/ui/Table';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { StatCard } from '@/components/ui/StatCard';
import { apiFetch } from '@/lib/api';

export function OrgDetails() {
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [org, setOrg] = React.useState<any>(null);
  const [invites, setInvites] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [orgRes, invRes] = await Promise.all([
          apiFetch(`/api/admin/orgs/${encodeURIComponent(id)}`),
          apiFetch(`/api/admin/orgs/${encodeURIComponent(id)}/invites`)
        ]);
        const orgJson = await orgRes.json();
        if (!orgJson.success) throw new Error(orgJson.error?.message || 'Failed to load org');
        const invJson = await invRes.json();
        if (!invJson.success) throw new Error(invJson.error?.message || 'Failed to load invites');
        setOrg(orgJson.data);
        setInvites(invJson.data || []);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (!id) return null;

  const inviteColumns = [
    {
      key: 'email',
      title: 'Email',
      sortable: true
    },
    {
      key: 'role',
      title: 'Role',
      render: (value: string) => <Badge variant="outline">{value}</Badge>
    },
    {
      key: 'status',
      title: 'Status',
      render: (_: any, record: any) => {
        const isAccepted = record.accepted_at;
        const isExpired = new Date(record.expires_at) < new Date();
        
        if (isAccepted) {
          return <Badge variant="success">Accepted</Badge>;
        } else if (isExpired) {
          return <Badge variant="error">Expired</Badge>;
        } else {
          return <Badge variant="warning">Pending</Badge>;
        }
      }
    },
    {
      key: 'created_at',
      title: 'Sent',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    },
    {
      key: 'expires_at',
      title: 'Expires',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        const isExpired = date < new Date();
        return (
          <div className={isExpired ? 'text-red-600' : ''}>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    }
  ];

  const statCards = org?.stats ? [
    {
      title: 'Members',
      value: org.memberCount,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      color: 'bg-blue-50 text-blue-600'
    },
    // Removed Assets and Templates cards; replaced Gallery Items with Attachments
    {
      title: 'Attachments',
      value: org.stats.attachmentsCount,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2v6a5 5 0 11-10 0V9a4 4 0 018 0v6a3 3 0 11-6 0V9" />
        </svg>
      ),
      color: 'bg-amber-50 text-amber-600'
    }
  ] : [];

  return (
    <PageContainer>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Link to="/admin/orgs" className="text-gray-500 hover:text-gray-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <span className="text-sm text-gray-500">Organizations</span>
          </div>
          {org && (
            <PageHeader
              title={org.name}
              className="mb-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-gray-600">{org.slug}</span>
                <Badge variant={org.status === 'active' ? 'success' : org.status === 'suspended' ? 'error' : 'warning'}>
                  {org.status}
                </Badge>
              </div>
            </PageHeader>
          )}
        </div>
        {org && (
          <div className="text-right">
            <div className="text-sm text-gray-500">Created</div>
            <div className="text-sm font-medium">{new Date(org.created_at).toLocaleDateString()}</div>
          </div>
        )}
      </div>

      {loading && <div className="text-center py-8 text-gray-500">Loading...</div>}
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      {org && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {statCards.map((card, index) => (
              <StatCard
                key={index}
                label={card.title}
                value={card.value}
                icon={card.icon}
                iconColor="default"
              />
            ))}
          </div>

          {/* Organization Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization Details</h2>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="text-sm text-gray-900">{org.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Slug</dt>
                <dd className="text-sm text-gray-900">{org.slug}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd>
                  <Badge variant={org.status === 'active' ? 'success' : org.status === 'suspended' ? 'error' : 'warning'}>
                    {org.status}
                  </Badge>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Plan</dt>
                <dd className="text-sm text-gray-900">{org.plan_key || 'Free'}</dd>
              </div>
              {org.trial_ends_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Trial Ends</dt>
                  <dd className="text-sm text-gray-900">{new Date(org.trial_ends_at).toLocaleDateString()}</dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="text-sm text-gray-900">{new Date(org.created_at).toLocaleString()}</dd>
              </div>
            </dl>
          </div>

          {/* Invitations */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Invitations</h2>
            <Table
              data={invites}
              columns={inviteColumns}
              searchPlaceholder="Search invites by email..."
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [5, 10, 20]
              }}
              emptyText="No invitations found."
            />
          </div>
        </div>
      )}
    </PageContainer>
  );
}


